import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useState, useEffect, useCallback } from 'react';
import CountrySelect from "../countryselect";
import { 
  Eye, EyeOff, User, Mail, Phone, Calendar, 
  MapPin, Home, CreditCard, Lock, Loader2 
} from 'lucide-react';

// Move InputField outside the component
const InputField = ({ type, name, placeholder, value, onChange, icon: Icon }) => (
  <div className="relative flex items-center mb-4">
    {Icon && <Icon className="absolute left-3 h-5 w-5 text-gray-400" />}
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
      required
    />
  </div>
);

function Signup() {
    const navigate = useNavigate();
    const { token } = useParams();
    const [formData, setFormData] = useState({
        username: "",
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        country: "",
        dob: "",
        address: "",
        city: "",
        state: "",
        gender: "",
        bankName: "",
        bankNumber: "",
        bankFullName: "",
        password: "",
        confirmPassword: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (localStorage.getItem("authUser")) {
            navigate("/dashboard");
        }
    }, [navigate]);

    // Use useCallback to memoize the function
    const handleChange = useCallback((e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    }, []);

    const registerHandler = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match");
            setTimeout(() => {
                setFormData(prev => ({ ...prev, password: "", confirmPassword: "" }));
                setError("");
                setIsLoading(false);
            }, 5000);
            return;
        }

        try {
            const response = await fetch(
                `https://portal-sanieldanproperties-api.onrender.com/auth/signup/${token}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData)
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error);
            }

            localStorage.setItem("authToken", data.Token);
            localStorage.setItem("authUser", JSON.stringify(data.user));

            setSuccess("Signup successful! Redirecting to Dashboard...");

            setTimeout(() => {
                navigate("/dashboard");
            }, 5000);
        } catch (err) {
            setError(err.message);
            setTimeout(() => {
                setError("");
                setIsLoading(false);
            }, 5000);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white py-12 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="flex justify-center items-center gap-2 text-4xl md:text-5xl font-bold">
                        <h1>New</h1>
                        <h1>Here?</h1>
                    </div>
                    <div className="flex justify-center my-4">
                        <div className="w-32 h-0.5 bg-white"></div>
                    </div>
                    <p className="max-w-2xl mx-auto text-lg px-4">
                        Sign up and discover great opportunities. Registration requires a valid referral link.
                    </p>
                </div>
            </div>

            {/* Notifications */}
            {(error || success) && (
                <div className={`fixed top-5 right-5 z-50 p-4 rounded-lg shadow-lg ${
                    error ? 'bg-red-50 border-l-4 border-red-500' : 'bg-green-50 border-l-4 border-green-500'
                }`}>
                    <p className={`text-sm ${error ? 'text-red-800' : 'text-green-800'}`}>
                        {error || success}
                    </p>
                </div>
            )}

            {/* Form Section */}
            <form onSubmit={registerHandler} className="max-w-2xl mx-auto px-4 py-8">
                <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
                    <h2 className="text-3xl font-bold text-red-600 mb-8">Sign Up</h2>

                    <div className="space-y-4">
                        {/* Personal Information */}
                        <InputField type="text" name="username" placeholder="Username" value={formData.username} onChange={handleChange} icon={User} />
                        <InputField type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} icon={Mail} />
                        <InputField type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} icon={User} />
                        <InputField type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} icon={User} />
                        <InputField type="tel" name="phoneNumber" placeholder="Phone Number" value={formData.phoneNumber} onChange={handleChange} icon={Phone} />
                        
                        {/* Address Information */}
                        <InputField type="date" name="dob" placeholder="Date of Birth" value={formData.dob} onChange={handleChange} icon={Calendar} />
                        <InputField type="text" name="address" placeholder="Address" value={formData.address} onChange={handleChange} icon={Home} />
                        <InputField type="text" name="city" placeholder="City" value={formData.city} onChange={handleChange} icon={MapPin} />
                        <InputField type="text" name="state" placeholder="State" value={formData.state} onChange={handleChange} icon={MapPin} />

                        {/* Country Select */}
                        <div className="relative flex items-center mb-4">
                            <MapPin className="absolute left-3 h-5 w-5 text-gray-400" />
                            <select
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                required
                            >
                                <option value="">Select Country</option>
                                {CountrySelect.map((country, index) => (
                                    <option key={index} value={country}>{country}</option>
                                ))}
                            </select>
                        </div>

                        {/* Gender Select */}
                        <div className="relative flex items-center mb-4">
                            <User className="absolute left-3 h-5 w-5 text-gray-400" />
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                required
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* Bank Information */}
                        <InputField type="text" name="bankName" placeholder="Bank Name" value={formData.bankName} onChange={handleChange} icon={CreditCard} />
                        <InputField type="text" name="bankNumber" placeholder="Bank Number" value={formData.bankNumber} onChange={handleChange} icon={CreditCard} />
                        <InputField type="text" name="bankFullName" placeholder="Bank Full Name" value={formData.bankFullName} onChange={handleChange} icon={User} />

                        {/* Password Fields */}
                        <div className="relative flex items-center mb-4">
                            <Lock className="absolute left-3 h-5 w-5 text-gray-400" />
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Password"
                                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                            </button>
                        </div>

                        <div className="relative flex items-center mb-4">
                            <Lock className="absolute left-3 h-5 w-5 text-gray-400" />
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="Confirm Password"
                                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2"
                            >
                                {showConfirmPassword ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                            </button>
                        </div>

                        {/* Remember Me Checkbox */}
                        <div className="flex items-center justify-end gap-2">
                            <input
                                type="checkbox"
                                id="rememberMe"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                            />
                            <label htmlFor="rememberMe" className="text-red-500 font-semibold">
                                Remember Me
                            </label>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg transition duration-200 flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                "Register"
                            )}
                        </button>

                        {/* Login Link */}
                        <p className="text-center text-gray-600 mt-4">
                            Have an account?{' '}
                            <Link to="/" className="text-red-500 hover:text-red-600 font-semibold">
                                Login
                            </Link>
                        </p>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default Signup;